import React from 'react';
import { default as RatingStars } from '@material-ui/lab/Rating';
import Typography from '@material-ui/core/Typography';
import { SharedService } from '@consumers/services';
import { ActionDialog, Input, showToast } from '@shared/components';
import { LoaderContext } from '@shared/contexts';
import { TOAST_TYPES, VALIDATIONS } from '@shared/constants';
import { showConfetti } from '@utils';

import StarIcon from '@assets/icons/star.svg';
import StarLightIcon from '@assets/icons/star-light.svg';
import StarEmptyIcon from '@assets/icons/star-empty.svg';
import StarEmptyLightIcon from '@assets/icons/star-empty-light.svg';

let timeout = null;
const noop = () => {};
const RATING_MESSAGES = {
  EXCELLENT: "You're pretty awesome yourself :)",
  GOOD: 'Thank you. You rock!',
  POST_AVERAGE: 'Thank you for your feedback :)',
  PRE_AVERAGE: "Can you please explain why it wasn't delightful?",
};

const defaultState = {
  rating: null,
  feedback: null,
  isSubmitted: false,
  isStarHovering: false,
  errorList: {},
};

export default ({
  isOpen = false,
  isOpenedFromPackage = false,
  onClose = noop,
}) => {
  const { showLoader, hideLoader } = React.useContext(LoaderContext);
  const [state, setState] = React.useState(defaultState);

  React.useEffect(() => {
    if (state.isSubmitted) {
      timeout = setTimeout(handleClose, 60 * 1000);
    }

    return () => clearTimeout(timeout);
  }, [state.isSubmitted]);

  const handleClose = () => {
    setState(prevState => ({
      ...prevState,
      ...defaultState,
    }));
    onClose();
  };

  const handleSubmit = async () => {
    showLoader();

    const { data, error } = await SharedService.submitRating(
      {
        stars_count: state.rating,
        comment: state.rating <= 3 ? state.feedback : null,
      },
      isOpenedFromPackage
    );

    if (error) {
      showToast(
        Array.isArray(error) ? error[0].message : error,
        TOAST_TYPES.ERROR
      );
      return hideLoader();
    }

    showToast('Submitted successfully.', TOAST_TYPES.SUCCESS);
    setState(prevState => ({
      ...prevState,
      isSubmitted: true,
    }));

    hideLoader();
    if (state.rating === 5) showConfetti(2 * 1000);
  };

  const isSubmitDisabled = ({
    rating = null,
    feedback = null,
    isSubmitted = false,
    errorList = {},
  }) => {
    if (isSubmitted) return true;
    if (rating > 3) return false;
    let isDisabled = rating !== null ? false : true;

    if (rating !== null && !feedback) {
      isDisabled = true;
    }

    Object.keys(errorList).map(key => errorList[key] && (isDisabled = true));
    return isDisabled;
  };

  const renderContent = () => {
    return (
      <div className='color-text-secondary'>
        <Typography variant='body1'>
          Are you enjoying Conductor? Let us know below!
        </Typography>
        <RatingStars
          readOnly={state.isSubmitted}
          value={state.rating}
          precision={0.5}
          size='large'
          icon={
            <img
              src={state.isStarHovering ? StarLightIcon : StarIcon}
              className='ml-1 mr-1'
            />
          }
          emptyIcon={
            <img
              src={state.isStarHovering ? StarEmptyLightIcon : StarEmptyIcon}
              className='ml-1 mr-1'
            />
          }
          classes={{
            root: 'mt-5',
          }}
          onChange={(evt, value) => {
            setState(prevState => ({
              ...prevState,
              rating: value,
            }));
          }}
          onChangeActive={(evt, value) => {
            setState(prevState => ({
              ...prevState,
              isStarHovering: value !== -1,
            }));
          }}
        />
        {((state.rating !== null && state.rating <= 3) ||
          state.isSubmitted) && (
          <Typography variant='body1' className='mt-4'>
            {state.isSubmitted
              ? state.rating > 3
                ? state.rating === 5
                  ? RATING_MESSAGES.EXCELLENT
                  : RATING_MESSAGES.GOOD
                : RATING_MESSAGES.POST_AVERAGE
              : RATING_MESSAGES.PRE_AVERAGE}
          </Typography>
        )}
        {state.rating !== null && state.rating <= 3 && (
          <Input
            disabled={state.isSubmitted}
            required
            multiline
            rows={4}
            value={state.feedback}
            variant='outlined'
            name='feedback'
            label='Comment'
            placeholder='Please leave your comment here.'
            className='w-100 mb-4 mt-4'
            validations={{
              [VALIDATIONS.REQUIRED]: true,
              [VALIDATIONS.MAX_LENGTH]: 255,
            }}
            onChange={(event, error) => {
              let value = event.currentTarget.value;
              let errorField = event.currentTarget.name;

              setState(prevState => ({
                ...prevState,
                feedback: value,
                errorList: {
                  ...prevState.errorList,
                  [errorField]: !!error,
                },
              }));
            }}
          />
        )}
      </div>
    );
  };

  return (
    <ActionDialog
      open={isOpen}
      heading='Rate App'
      positiveActionLabel='Submit'
      negativeActionLabel='Cancel'
      content={renderContent()}
      onConfirm={handleSubmit}
      onCancel={handleClose}
      onClose={handleClose}
      isConfirmDisabled={isSubmitDisabled(state)}
      classes={{
        confirm: 'color-white',
      }}
    />
  );
};
